import apiClient from './api';
import {
  Law,
  CreateLawRequest,
  UpdateLawRequest,
  ApproveLawRequest,
  CompareVersionsRequest,
  CompareVersionsResponse,
  SearchLawsRequest,
  SearchLawsResponse,
  LawVersion,
} from '../types';

export const lawService = {
  // 모든 법령 조회
  async getAllLaws(): Promise<Law[]> {
    const response = await apiClient.get<Law[]>('/laws');
    return response.data;
  },

  // 승인된 법령만 조회
  async getApprovedLaws(): Promise<Law[]> {
    const response = await apiClient.get<Law[]>('/laws/approved');
    return response.data;
  },

  // 일반 사용자용: 승인된 법령과 미승인 법령의 마지막 승인된 버전 조회
  async getLawsForGeneralUser(): Promise<Law[]> {
    const response = await apiClient.get<Law[]>('/laws/general');
    return response.data;
  },

  // 승인 대기 중인 법령 조회
  async getPendingLaws(): Promise<Law[]> {
    const response = await apiClient.get<Law[]>('/laws/pending');
    return response.data;
  },

  // 특정 법령 조회
  async getLaw(lawId: string): Promise<Law> {
    const response = await apiClient.get<Law>(`/laws/${lawId}`);
    return response.data;
  },

  // 법령 생성
  async createLaw(lawData: CreateLawRequest): Promise<Law> {
    const response = await apiClient.post<Law>('/laws', lawData);
    return response.data;
  },

  // 법령 수정
  async updateLaw(lawId: string, lawData: UpdateLawRequest): Promise<Law> {
    const response = await apiClient.patch<Law>(`/laws/${lawId}`, lawData);
    return response.data;
  },

  // 법령 삭제
  async deleteLaw(lawId: string): Promise<void> {
    await apiClient.delete(`/laws/${lawId}`);
  },

  // 법령 승인/승인취소
  async approveLaw(lawId: string, approveData: ApproveLawRequest): Promise<Law> {
    const response = await apiClient.patch<Law>(`/laws/${lawId}/approve`, approveData);
    return response.data;
  },

  // 법령 버전 이력 조회
  async getLawVersions(lawId: string): Promise<LawVersion[]> {
    const response = await apiClient.get<LawVersion[]>(`/laws/${lawId}/versions`);
    return response.data;
  },

  // 법령 버전 비교
  async compareVersions(lawId: string, compareData: CompareVersionsRequest): Promise<CompareVersionsResponse> {
    const response = await apiClient.post<CompareVersionsResponse>(`/laws/${lawId}/compare`, compareData);
    return response.data;
  },

  // 법령 버전 삭제
  async deleteLawVersion(lawId: string, versionId: string): Promise<void> {
    await apiClient.delete(`/laws/${lawId}/versions/${versionId}`);
  },

  // 고급 검색
  async searchLaws(searchParams: SearchLawsRequest): Promise<SearchLawsResponse> {
    const params = new URLSearchParams();
    
    if (searchParams.query) params.append('query', searchParams.query);
    if (searchParams.searchType) params.append('searchType', searchParams.searchType);
    if (searchParams.searchField) params.append('searchField', searchParams.searchField);
    if (searchParams.approvedOnly !== undefined) params.append('approvedOnly', searchParams.approvedOnly.toString());
    if (searchParams.limit) params.append('limit', searchParams.limit.toString());
    if (searchParams.offset) params.append('offset', searchParams.offset.toString());
    if (searchParams.sortBy) params.append('sortBy', searchParams.sortBy);
    if (searchParams.sortOrder) params.append('sortOrder', searchParams.sortOrder);

    const response = await apiClient.get<SearchLawsResponse>(`/laws/search?${params.toString()}`);
    return response.data;
  },

  // 간단한 검색
  async searchLawsSimple(query: string): Promise<Law[]> {
    const response = await apiClient.get<Law[]>(`/laws/search/simple?q=${encodeURIComponent(query)}`);
    return response.data;
  },

  // 최근 법령 조회
  async getRecentLaws(limit: number = 3): Promise<Law[]> {
    const response = await apiClient.get<Law[]>(`/laws?limit=${limit}&sortBy=createdAt&sortOrder=desc`);
    return response.data;
  },

  // 첨부파일 변경으로 인한 버전 이력 생성
  async createVersionForAttachmentsChange(lawId: string): Promise<Law> {
    const response = await apiClient.post<Law>(`/laws/${lawId}/attachments-changed`);
    return response.data;
  },
}; 